import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

import { AnnotationService } from '../../annotation-container/annotation.service';
import { ToolsService } from '../../services/tools.service';
import { ViewerCoreService } from '../../services/viewer-core.service';
import { ButtonComponent } from '../../ui/button/button.component';

@Component({
  selector: 'app-measurements-panel',
  imports: [CommonModule, ButtonComponent, TranslocoPipe],
  templateUrl: './measurements-panel.component.html',
  styleUrls: ['./measurements-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementsPanelComponent {
  protected annotationService = inject(AnnotationService);
  protected tools = inject(ToolsService);
  private core = inject(ViewerCoreService);

  // Scene tree state
  scenePointClouds = signal<{ id: string; name: string; visible: boolean }[]>([]);
  sceneMeasurements = signal<{ id: string; name: string; visible: boolean }[]>([]);
  sceneProfiles = signal<{ id: string; name: string; visible: boolean }[]>([]);
  sceneVolumes = signal<{ id: string; name: string; visible: boolean }[]>([]);

  selectedMeasurementId = signal<string | null>(null);
  selectedMeasurementType = signal<string | null>(null);
  private readonly _selectedMeasurementDetails = signal<{
    coordinates?: { x: number; y: number; z: number }[];
    distances?: number[];
    totalDistance?: number;
    area?: number;
    angles?: { label: string; value: number }[];
    volume?: number;
    dimensions?: { x: number; y: number; z: number };
    height?: number;
    radius?: number;
    circumference?: number;
    position?: { x: number; y: number; z: number };
    title?: string;
    description?: string;
  } | null>(null);
  get selectedMeasurementDetails() {
    return this._selectedMeasurementDetails();
  }

  @Output() profile2DClick = new EventEmitter<{ event: unknown; id: string }>();

  constructor() {
    effect(onCleanup => {
      const viewer: any = this.core.viewer();
      if (!viewer?.scene) return;

      const sceneEvents = [
        'pointcloud_added',
        'measurement_added',
        'measurement_removed',
        'profile_added',
        'profile_removed',
        'volume_added',
        'volume_removed',
      ];
      const rebuild = (): void => this.rebuildSceneState(viewer);
      let currentScene = viewer.scene;

      const attachSceneListeners = (scene: any): void => {
        if (!scene) return;
        sceneEvents.forEach((eventName: string) => scene.addEventListener?.(eventName, rebuild));
      };

      const detachSceneListeners = (scene: any): void => {
        if (!scene) return;
        sceneEvents.forEach((eventName: string) => scene.removeEventListener?.(eventName, rebuild));
      };

      const handleSceneChanged = (event: any): void => {
        detachSceneListeners(currentScene);
        currentScene = event.scene ?? viewer.scene;
        attachSceneListeners(currentScene);
        rebuild();
      };

      attachSceneListeners(currentScene);
      viewer.addEventListener?.('update', rebuild);
      viewer.addEventListener?.('scene_changed', handleSceneChanged);
      rebuild();

      onCleanup(() => {
        viewer.removeEventListener?.('update', rebuild);
        viewer.removeEventListener?.('scene_changed', handleSceneChanged);
        detachSceneListeners(currentScene);
      });
    });
  }

  // Measurement actions
  startDistance(): void {
    this.tools.startDistanceMeasurement();
  }

  startAngle(): void {
    this.tools.startAngleMeasurement();
  }

  startPoint(): void {
    this.tools.startPointMeasurement();
  }

  startHeight(): void {
    this.tools.startHeightMeasurement();
  }

  startCircle(): void {
    this.tools.startCircleMeasurement();
  }

  startAzimuth(): void {
    this.tools.startAzimuthMeasurement();
  }

  startArea(): void {
    this.tools.startAreaMeasurement();
  }

  startProfile(): void {
    this.tools.startProfile();
  }

  startVolume(): void {
    this.tools.startVolume();
  }

  startSphereVolume(): void {
    this.tools.startSphereVolume();
  }

  startAnnotation(): void {
    this.tools.startAnnotation();
  }

  toggleMeasurementVisibility(kind: 'measurements' | 'volumes' | 'profiles', id: string): void {
    const v: any = this.core.viewer();
    if (!v) return;
    const scene = v.scene;

    const lists: Record<string, any[]> = {
      measurements: scene.measurements || [],
      profiles: scene.profiles || [],
      volumes: scene.volumes || [],
    };
    const list = lists[kind];
    if (!list) return;
    const obj = list.find((o: { uuid?: string; id?: string }) => (o.uuid || o.id) === id);
    if (obj) {
      obj.visible = !obj.visible;
      v.dispatchEvent?.({ type: 'update' });
    }
    this.rebuildSceneState(v);
  }

  removeMeasurement(kind: 'measurement' | 'volume' | 'profile', id: string): void {
    switch (kind) {
      case 'measurement':
        this.tools.removeMeasurement(id);
        break;
      case 'volume':
        this.tools.removeVolume(id);
        break;
      case 'profile':
        this.tools.removeProfile(id);
        break;
    }
    if (this.selectedMeasurementId() === id && this.selectedMeasurementType() === kind) {
      this.selectedMeasurementId.set(null);
      this.selectedMeasurementType.set(null);
      this._selectedMeasurementDetails.set(null);
    }
    this.rebuildSceneState();
  }

  selectMeasurement(type: string, id: string): void {
    if (this.selectedMeasurementId() === id && this.selectedMeasurementType() === type) {
      this.selectedMeasurementId.set(null);
      this.selectedMeasurementType.set(null);
      this._selectedMeasurementDetails.set(null);
      return;
    }

    this.selectedMeasurementId.set(id);
    this.selectedMeasurementType.set(type);
    const details = this.tools.getMeasurementDetails(type, id);
    if (type === 'annotation' && details) {
      details.title = this.annotationService.getTitle(id);
      details.description = this.annotationService.getDescription(id);
    }
    this._selectedMeasurementDetails.set(details);
  }

  toggleAnnotationVisibility(uuid: string): void {
    this.annotationService.toggleAnnotationVisibility(uuid);
  }

  removeAnnotation(uuid: string): void {
    this.annotationService.removeAnnotation(uuid);
  }

  onAnnotationTitleChange(value: string): void {
    if (this.selectedMeasurementId() && this.selectedMeasurementType() === 'annotation') {
      this.annotationService.updateTitle(this.selectedMeasurementId()!, value);
      this._selectedMeasurementDetails.update(d => (d ? { ...d, title: value } : null));
      this.rebuildSceneState();
    }
  }

  onAnnotationDescriptionChange(value: string): void {
    if (this.selectedMeasurementId() && this.selectedMeasurementType() === 'annotation') {
      this.annotationService.updateDescription(this.selectedMeasurementId()!, value);
      this._selectedMeasurementDetails.update(d => (d ? { ...d, description: value } : null));
    }
  }

  private rebuildSceneState(viewer: any = this.core.viewer()): void {
    if (!viewer?.scene) return;

    try {
      const scene = viewer.scene;

      this.scenePointClouds.set(
        (scene.pointclouds || []).map((pc: any, i: number) => ({
          id: pc.uuid || `pc-${i}`,
          name: pc.name || pc.geometry?.name || `PointCloud ${i + 1}`,
          visible: pc.visible !== false,
        })),
      );

      this.sceneMeasurements.set(
        (scene.measurements || []).map((measurement: any, i: number) => ({
          id: measurement.uuid || `m-${i}`,
          name: measurement.name || `Measurement ${i + 1}`,
          visible: measurement.visible !== false,
        })),
      );

      this.sceneProfiles.set(
        (scene.profiles || []).map((profile: any, i: number) => ({
          id: profile.uuid || `p-${i}`,
          name: profile.name || `Profile ${i + 1}`,
          visible: profile.visible !== false,
        })),
      );

      this.sceneVolumes.set(
        (scene.volumes || [])
          .filter((volume: any) => !volume.clip)
          .map((volume: any, i: number) => ({
            id: volume.uuid || `v-${i}`,
            name: volume.name || `Volume ${i + 1}`,
            visible: volume.visible !== false,
          })),
      );
    } catch {
      console.warn('Failed to rebuild scene state, possibly due to missing viewer or scene.');
    }
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }
}
